import { Stack, useMediaQuery, useTheme } from "@mui/material"
import * as Sentry from "@sentry/react"
import { getAuth, signInAnonymously } from "firebase/auth"
import React, { useEffect, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { AgentView } from "../../components/AgentView"
import Background from "../../components/background"
import { Header } from "../../components/header"
import { Loader } from "../../components/loader"
import { SnackbarWithText } from "../../components/snackbar"
import { useCustomBranding } from "../../constants/colors"
import { addCallDb } from "../../external/firestore"
import { initialAgent } from "../../types/Agent"
import { AnalyticsEvents } from "../../types/Analytics"
import { StateAction } from "../../types/State"
import { SnackbarType } from "../../types/UI"
import { getAvailableCredits, isAnonymous, isInIframe } from "../../utils/func"
import {
  ApiError,
  getAgent,
  getAgentUsage,
  getCapacity,
  getDefaultAgentId,
  getDefaultCreatorId,
} from "../../utils/general"
import { useAppContext, useAppDispatchContext } from "../Context"
import { useMixpanel } from "../hooks/useMixpanel"

export function PreScreenCustomBranding() {
  const [permissionsGranted, setPermissionsGranted] = useState(false)
  const [capacity, setCapacity] = useState<boolean>(false)
  const [overUsage, setOverUsage] = useState<boolean>(false)
  const [agentError, setAgentError] = useState<string>("")
  const [agentExists, setAgentExists] = useState<boolean>(true)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [joinModalOpen, setJoinModalOpen] = useState<boolean>(false)
  const [loginModalOpen, setLoginModalOpen] = useState<boolean>(false)

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const { isEnabled, isBeyEnabled } = useCustomBranding()

  const auth = getAuth()

  const { state } = useLocation()
  const location = useLocation()
  const context = useAppContext()
  const { user, stream, agent, call } = context
  const { id } = useParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatchContext()
  const isUserAnonymous = isAnonymous()
  const isEmbed = isInIframe()
  const { logAnalyticsEvent } = useMixpanel()

  useEffect(() => {
    setPermissionsGranted(false)

    if (stream instanceof MediaStream) {
      stream.getTracks().forEach((track) => track.stop())
    }

    dispatch({ type: StateAction.end })
    dispatch({ type: StateAction.setAudioDevice, payload: null })
    dispatch({ type: StateAction.setVideoDevice, payload: null })
  }, [location.pathname, location.search])

  useEffect(() => {
    if (state && user) {
      navigate(`/${state}`)
    }
  }, [state, user])

  useEffect(() => {
    if (!user) {
      setLoginModalOpen(true)
    } else {
      setLoginModalOpen(false)
    }
  }, [user])

  useEffect(() => {
    const initialize = async () => {
      try {
        const result = await signInAnonymously(auth)

        if (!result.user) {
          setAgentError("Failed to authenticate user")
          return
        }

        const agentId = id || getDefaultAgentId

        const fetchedAgent = await getAgent(agentId)
        setAgentExists(true)
        dispatch({ type: StateAction.setAgent, payload: fetchedAgent })

        if (
          agentId === getDefaultAgentId ||
          fetchedAgent.creatorId === getDefaultCreatorId
        ) {
          dispatch({ type: StateAction.setAgentId, payload: agentId })
          dispatch({ type: StateAction.setAgentUsage, payload: null })
        } else {
          const usage = await getAgentUsage(agentId)

          if (usage.pay_as_you_go_enabled) {
            dispatch({
              type: StateAction.setAgentUsage,
              payload: {
                pay_as_you_go_enabled: usage.pay_as_you_go_enabled,
                current_usage: usage.current_usage,
                usage_limit: null,
                subscription_plan: usage.subscription_plan,
              },
            })
          } else {
            dispatch({ type: StateAction.setAgentUsage, payload: usage })
            const remainingCredits = getAvailableCredits(usage)
            setOverUsage(remainingCredits <= 0)
          }
        }
      } catch (error) {
        console.error("Error during initialization:", error)
        Sentry.captureException(error)
        if (error instanceof ApiError && error.status === 404) {
          setAgentExists(false)
          dispatch({ type: StateAction.setAgent, payload: initialAgent })
        } else {
          setAgentError("Error initializing the application")
        }
      } finally {
        setIsLoading(false)
      }
    }
    initialize()
  }, [id, user])

  const sendAnalyticsEvent = () => {
    logAnalyticsEvent(AnalyticsEvents.AgentVisited)
  }

  useEffect(() => {
    sendAnalyticsEvent()
  }, [sendAnalyticsEvent])

  useEffect(() => {
    const req = async () => {
      try {
        if (user) {
          const res = await getCapacity()
          setCapacity(res)
        }
      } catch (error) {
        console.error("error fetching capacity", error)
      }
    }
    req()
  }, [user])

  const onJoinCall = async () => {
    try {
      const agentId = id || getDefaultAgentId
      const conversationId = await addCallDb(user, agentId, agent)

      dispatch({
        type: StateAction.setCall,
        payload: {
          ...call,
          id: conversationId,
          agentId: agentId,
          chatMessages: [],
          startedAt: new Date(),
        },
      })
      dispatch({ type: StateAction.setCustomBrandingFlow, payload: true })

      logAnalyticsEvent(AnalyticsEvents.MeetingJoined, {
        call_id: conversationId,
      })
      navigate(`/rooms/${conversationId}`)
    } catch (error) {
      console.error("error joining call", error)
    }
  }

  if (isLoading) {
    return <Loader />
  }

  return (
    <Stack
      direction={"column"}
      position={"relative"}
      height={"100vh"}
      width={"100vw"}
      overflow={"hidden"}
    >
      {!isEmbed && <Header />}
      <SnackbarWithText
        open={agentError !== ""}
        severity={SnackbarType.error}
        text={agentError}
        handleClose={() => setAgentError("")}
      />
      <Stack
        direction={"column"}
        spacing={2}
        sx={{
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          width: "100%",
          height: isEmbed || isMobile ? "100vh" : "calc(100vh - 85px)", // Adjust height based on embed mode
          px: isEmbed ? 0 : { xs: 1, sm: 2, md: 2, lg: 3, xl: 4 },
          overflow: "hidden",
          position: "relative",
          zIndex: 1,
        }}
      >
        <AgentView
          agent={agent}
          permissionsGranted={permissionsGranted}
          setPermissionsGranted={setPermissionsGranted}
          onJoinCall={onJoinCall}
          isUserAnonymous={isUserAnonymous}
          joinMeetingModalProps={{
            open: joinModalOpen,
            onClose: () => setJoinModalOpen(false),
          }}
          loginModalProps={{
            open: loginModalOpen,
            onOpen: () => setLoginModalOpen(true),
            onClose: () => setLoginModalOpen(false),
          }}
          isEmbed={isEmbed}
          agentExists={agentExists}
        />
      </Stack>
      <Background />
    </Stack>
  )
}
